import React, { useMemo } from 'react';
import { View, TouchableOpacity, StyleSheet } from 'react-native';
import { Text } from '@/components/common/text';
import { useMarketplaceColors } from '@/constants/residents/marketplaceColors';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';

const ChatIcon = RESIDENTS_SVG_COMPONENTS.CONVERSATION;
const FlagIcon = RESIDENTS_SVG_COMPONENTS.FINISH;

interface ActionButtonRowProps {
  onContactPress?: () => void;
  onReportPress?: () => void;
  contactLabel?: string;
  showReportButton?: boolean;
  hideContactButton?: boolean;
}

/**
 * Reusable action button row with contact and report buttons
 */
export const ActionButtonRow: React.FC<ActionButtonRowProps> = ({
  onContactPress,
  onReportPress,
  contactLabel = 'Contact seller',
  showReportButton = true,
  hideContactButton = false,
}) => {
  const mc = useMarketplaceColors();

  const styles = useMemo(() => StyleSheet.create({
    container: { flexDirection: 'row', gap: 16 },
    contactButton: {
      flex: 1, minHeight: 34, paddingVertical: 8, backgroundColor: mc.buttonPrimary, borderRadius: 8, // 34 − 18 icon, split
      flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 6, paddingHorizontal: 16,
    },
    contactButtonText: { fontSize: 12, fontWeight: '500', color: mc.buttonPrimaryText },
    reportButton: {
      width: 34, minHeight: 34, paddingVertical: 7, backgroundColor: mc.white5Percent, borderRadius: 8, // 34 − 20 icon, split
      alignItems: 'center', justifyContent: 'center', flexDirection: 'row', gap: 6,
    },
    reportButtonFullWidth: { flex: 1, width: 'auto', paddingHorizontal: 16 },
    reportButtonText: { fontSize: 12, fontWeight: '500', color: mc.textSecondary },
  }), [mc]);

  return (
    <View style={styles.container}>
      {!hideContactButton && onContactPress && (
        <TouchableOpacity style={styles.contactButton} onPress={onContactPress} activeOpacity={0.8}>
          <ChatIcon width={18} height={18} color={mc.buttonPrimaryText} />
          <Text style={styles.contactButtonText} numberOfLines={2}>{contactLabel}</Text>
        </TouchableOpacity>
      )}

      {showReportButton && onReportPress && (
        <TouchableOpacity style={[styles.reportButton, hideContactButton && styles.reportButtonFullWidth]} onPress={onReportPress} activeOpacity={0.8}>
          <FlagIcon width={20} height={20} color={mc.textPrimary} />
          {hideContactButton && (
            <Text style={styles.reportButtonText}>Report listing</Text>
          )}
        </TouchableOpacity>
      )}
    </View>
  );
};
